import React, { Component } from "react";
import { Button } from "reactstrap";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "./ag-theme-ela.css";

import { FetchData } from "./utils/DataFetch";
import { GetCookie } from "./utils/Common";
import { TranslationBillingForm } from "./TranslationBillingForm";

import "./Translation.css";

const newLine = (jobId, language) => ({
  EventType: "insert",
  JobTransQuoteLineId: null,
  JobId: jobId,
  Subject: "",
  Language: language ?? "",
  WordCount: "",
  CostPerUnit: "",
  Amount: "",
  CreateByUserId: null,
  IsDeletedFlg: false,
});

const formatCostPerUnit = (value) =>
  value === null || value === undefined ? "" : `${Number(value).toFixed(3).replace(/^0(?=\.)/, "")} pw`;

const formatMoney = (value) =>
  Number(value ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const sumAmounts = (lines) =>
  Math.round(lines.reduce((acc, l) => acc + (Number(l.amount) || 0), 0) * 100) / 100;

const ActionsCell = ({ data, node, context }) =>
  context.readOnly || node.rowPinned ? null : (
    <span className="tr-grid-actions">
      <button
        type="button"
        className="tr-grid-link"
        onClick={() => context.edit(data)}
        aria-label={`Edit quote line ${data.subject ?? ""}`}
      >
        Edit
      </button>
      <button
        type="button"
        className="tr-grid-link tr-grid-link-danger"
        onClick={() => context.remove(data)}
        aria-label={`Delete quote line ${data.subject ?? ""}`}
      >
        Delete
      </button>
    </span>
  );

// Same layout as the work order's receivables grid: actions first, total pinned at the bottom.
const columns = [
  { headerName: "", colId: "actions", width: 100, cellRenderer: ActionsCell, sortable: false },
  { headerName: "Subject", field: "subject", flex: 1, minWidth: 200, tooltipField: "subject" },
  { headerName: "Language", field: "language", width: 120 },
  { headerName: "Word Count", field: "wordCount", width: 110, type: "rightAligned" },
  {
    headerName: "Cost Per Unit",
    field: "costPerUnit",
    width: 120,
    type: "rightAligned",
    valueFormatter: (p) => (p.node.rowPinned ? p.value : formatCostPerUnit(p.value)),
    cellStyle: (p) => (p.node.rowPinned ? { fontWeight: "bold" } : null),
  },
  {
    headerName: "Unit Cost",
    field: "amount",
    width: 110,
    type: "rightAligned",
    valueFormatter: (p) => (!p.node.rowPinned && Number(p.value) === 0 ? "N/C" : formatMoney(p.value)),
    cellClassRules: { "text-danger": (p) => Number(p.value) < 0 },
    cellStyle: (p) => (p.node.rowPinned ? { fontWeight: "bold" } : null),
  },
];

const pinnedRowStyle = (p) => (p.node.rowPinned ? { backgroundColor: "rgb(242, 242, 242)" } : null);

const defaultColDef = {
  resizable: true,
  sortable: true,
};

/**
 * Quote lines for a translation job (JobTransQuoteLine rows), as printed on the quote PDF.
 * Reports the lines' total so the form's Job total follows them, as the work order form does.
 */
export class TranslationBilling extends Component {
  static displayName = TranslationBilling.name;

  constructor(props) {
    super(props);
    this.user = JSON.parse(GetCookie("user"));
    this.state = { lines: [], loading: true, editing: null };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    FetchData("/getjobtransquotelines", "POST", this.user.token, { JobId: this.props.jobId }).then((data) => {
      const lines = Array.isArray(data) ? data : [];
      this.setState({ lines, loading: false });
      this.props.onTotalChange?.(sumAmounts(lines));
    });
  };

  announce = (message) => this.props.onMessage?.(message);

  edit = (row) =>
    this.setState({
      editing: {
        EventType: "update",
        JobTransQuoteLineId: row.jobTransQuoteLineId,
        JobId: row.jobId,
        Subject: row.subject ?? "",
        Language: row.language ?? "",
        WordCount: row.wordCount ?? "",
        CostPerUnit: row.costPerUnit ?? "",
        Amount: row.amount ?? "",
        CreateByUserId: row.createByUserId,
        IsDeletedFlg: row.isDeletedFlg,
      },
    });

  remove = (row) => {
    if (!window.confirm(`Delete the quote line "${row.subject || ""}"?`)) return;

    FetchData("/savejobtransquoteline", "POST", this.user.token, {
      EventType: "delete",
      JobTransQuoteLineId: row.jobTransQuoteLineId,
      JobId: row.jobId,
      IsDeletedFlg: row.isDeletedFlg,
      UpdateByUserId: this.user.userid,
    }).then((data) => {
      if (data === "Success") {
        this.announce("Quote line deleted.");
        this.load();
      } else {
        this.announce(data || "Could not delete the quote line.");
      }
    });
  };

  onSaved = (message) => {
    this.setState({ editing: null });
    this.announce(message);
    this.load();
  };

  render() {
    const { lines, loading, editing } = this.state;
    const { jobId, readOnly, language } = this.props;

    return (
      <section className="tr-section" aria-labelledby="tr-sec-billing">
        <div className="tr-section-header">
          <h3 id="tr-sec-billing" className="tr-section-title">
            Quote
            <span className="tr-column-count ms-2" aria-label={`${lines.length} lines`}>
              {lines.length}
            </span>
          </h3>
          {!readOnly && (
            <Button size="sm" color="primary" outline onClick={() => this.setState({ editing: newLine(jobId, language) })}>
              + Line
            </Button>
          )}
        </div>

        <div className="ag-theme-balham tr-history-grid" style={{ width: "100%", height: 150 }}>
          <AgGridReact
            columnDefs={columns}
            rowData={loading ? null : lines}
            defaultColDef={defaultColDef}
            pinnedBottomRowData={loading ? [] : [{ costPerUnit: "Total:", amount: sumAmounts(lines) }]}
            getRowStyle={pinnedRowStyle}
            rowHeight={26}
            headerHeight={28}
            context={{ edit: this.edit, remove: this.remove, readOnly }}
            onRowDoubleClicked={(p) => !readOnly && !p.node.rowPinned && this.edit(p.data)}
            enableBrowserTooltips={true}
            overlayNoRowsTemplate='<span class="tr-hint">No quote lines yet. These print on the quote.</span>'
            overlayLoadingTemplate='<span class="tr-hint">Loading quote…</span>'
          />
        </div>

        {editing && (
          <TranslationBillingForm
            model={editing}
            onSaved={this.onSaved}
            onCancel={() => this.setState({ editing: null })}
          />
        )}
      </section>
    );
  }
}
